import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Loader2, Rocket, Users, ImageIcon, Mail, Link2, Palette,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Launch full campaign (P1) — the 1-click orchestrator.
 *
 * Chains the three manual steps the AI Post Manager already supports into a
 * single action against POST /api/campaigns/launch-full:
 *   1) Save previewed posts as a campaign (drafts)
 *   2) Approve + schedule them to the chosen destinations + queue AI images
 *   3) Draft matching emails (newsletter + nurture) and (optionally) wire a
 *      list's auto-enroll automation
 *
 * Props:
 *   open, onOpenChange
 *   items            — previewItems from the AI Post Manager
 *   campaignPayload  — buildPayload() result (campaign metadata)
 *   blueprint        — strategy blueprint (optional)
 *   goal             — selected goal key (or 'none')
 *   emailCtaUrl      — tracked funnel URL for emails (optional)
 *   emailLists       — [{id,name}] for the auto-enroll picker
 */
export const LaunchCampaignDialog = ({
  open,
  onOpenChange,
  items = [],
  campaignPayload = {},
  blueprint = null,
  goal = 'none',
  emailCtaUrl = null,
  emailLists = [],
}) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({}); // platform -> [keys]
  const [genImages, setGenImages] = useState(true);
  const [brandFrame, setBrandFrame] = useState(false);
  const [brandKits, setBrandKits] = useState([]);
  const [brandKitId, setBrandKitId] = useState('none');
  const [draftEmails, setDraftEmails] = useState(true);
  const [autoEnroll, setAutoEnroll] = useState('none');

  const keyOf = (t) => `${t.platform}::${t.account_id}`;

  // Distinct normalized platforms present in the preview items
  const draftPlatforms = useMemo(
    () => Array.from(new Set(items.map((it) => String(it.platform).toLowerCase().replace(/\s+/g, '_')))),
    [items],
  );

  const targetsByPlatform = useMemo(() => {
    const map = {};
    for (const t of targets) (map[t.platform] = map[t.platform] || []).push(t);
    return map;
  }, [targets]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/connections/targets').then((res) => {
      const list = res.data?.targets || [];
      setTargets(list);
      // Auto-select first matching destination per platform so 1-click works
      // even without touching the checkboxes.
      const auto = {};
      for (const p of draftPlatforms) {
        const first = list.find((t) => t.platform === p);
        if (first) auto[p] = [keyOf(first)];
      }
      setSelected(auto);
    }).catch(() => { /* silent — backend falls back to legacy flip */ })
      .finally(() => setLoading(false));
    // Load brand kits for the brand-frame option.
    api.get('/brand-kits').then((res) => {
      const kits = res.data?.brand_kits || [];
      setBrandKits(kits);
      if (kits[0]) { setBrandKitId(kits[0].id); setBrandFrame(true); }
    }).catch(() => {});
  }, [open, api, draftPlatforms]);

  const toggle = (platform, key) => {
    setSelected((cur) => {
      const arr = cur[platform] || [];
      const next = arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key];
      return { ...cur, [platform]: next };
    });
  };

  const totalScheduled = useMemo(() => {
    let total = 0;
    const counts = {};
    for (const p of draftPlatforms) counts[p] = items.filter(
      (it) => String(it.platform).toLowerCase().replace(/\s+/g, '_') === p).length;
    for (const [p, c] of Object.entries(counts)) {
      total += c * ((selected[p] || []).length || 1);
    }
    return total;
  }, [items, draftPlatforms, selected]);

  const handleLaunch = async () => {
    setBusy(true);
    try {
      const targets_by_platform = {};
      for (const [platform, keys] of Object.entries(selected)) {
        if (!keys?.length) continue;
        targets_by_platform[platform] = keys
          .map((k) => targets.find((t) => keyOf(t) === k))
          .filter(Boolean);
      }
      const payload = {
        ...campaignPayload,
        items,
        goal: goal !== 'none' ? goal : null,
        blueprint: blueprint || null,
        targets_by_platform,
        generate_images: genImages,
        image_model: 'nano-banana',
        brand_frame: genImages && brandFrame && brandKitId !== 'none',
        brand_kit_id: brandKitId !== 'none' ? brandKitId : null,
        creative_size: '1:1',
        draft_emails: draftEmails,
        auto_enroll_list_id: autoEnroll !== 'none' ? autoEnroll : null,
        email_cta_url: emailCtaUrl || null,
      };
      const r = await api.post('/campaigns/launch-full', payload);
      const d = r.data || {};
      const parts = [`${d.total_scheduled || d.posts_saved} posts scheduled`];
      if (d.image_generation === 'queued') parts.push(d.brand_frame ? 'branded images generating' : 'images generating');
      if (d.emails && !d.emails.error) {
        parts.push(`emails drafted${d.emails.auto_enroll_list_id ? ' + auto-enroll wired' : ''}`);
      }
      toast.success(`Campaign launched — ${parts.join(' · ')}`);
      if (d.emails?.error) toast.message(`Emails skipped: ${d.emails.error}`);
      onOpenChange(false);
      navigate(`/posts?campaign=${d.campaign_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Launch failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="launch-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-indigo-600" />
            Launch full campaign
          </DialogTitle>
          <DialogDescription>
            One click wires the whole funnel: schedule the posts, generate images, and draft a
            matching email sequence — Social → Lead capture → Email → Sale.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Destinations */}
            <div className="space-y-2 max-h-56 overflow-y-auto">
              <p className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-indigo-600" /> Destinations
              </p>
              {draftPlatforms.map((platform) => {
                const list = targetsByPlatform[platform] || [];
                const sel = selected[platform] || [];
                return (
                  <div key={platform} data-testid={`launch-platform-${platform}`}>
                    <div className="text-[11px] font-medium text-zinc-600 capitalize mb-1 flex items-center gap-2">
                      <span className="capitalize">{platform.replace('_', ' ')}</span>
                      {sel.length > 0 && (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                          {sel.length} selected
                        </Badge>
                      )}
                    </div>
                    {list.length === 0 ? (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        No connected destination — posts schedule with their platform setting.
                      </p>
                    ) : (
                      <div className="rounded-md border border-zinc-200 divide-y divide-zinc-100">
                        {list.map((t) => {
                          const k = keyOf(t);
                          return (
                            <label
                              key={k}
                              className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-50 cursor-pointer"
                              data-testid={`launch-target-${platform}-${t.account_id}`}
                            >
                              <Checkbox
                                checked={sel.includes(k)}
                                onCheckedChange={() => toggle(platform, k)}
                                className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                              />
                              <span className="flex-1 truncate text-zinc-700">{t.account_name}</span>
                              {t.kind === 'org' && (
                                <span className="text-[9px] text-sky-700 bg-sky-50 px-1 rounded">Company</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Options */}
            <label className="flex items-start gap-2.5 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2.5 cursor-pointer">
              <Checkbox
                checked={genImages}
                onCheckedChange={(v) => setGenImages(!!v)}
                className="mt-0.5 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                data-testid="launch-genimages-toggle"
              />
              <div className="flex-1">
                <span className="text-xs font-semibold text-indigo-900 flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-indigo-600" /> Auto-generate images
                </span>
                <span className="block text-[11px] text-indigo-700/80 mt-0.5">
                  Nano Banana (Gemini · free on every account). Runs in the background, counts toward your image quota.
                </span>
                {genImages && (
                  <div className="mt-2.5 rounded-md border border-indigo-200 bg-white/70 p-2.5" data-testid="launch-brandframe-block">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox
                        checked={brandFrame}
                        onCheckedChange={(v) => setBrandFrame(!!v)}
                        disabled={brandKits.length === 0}
                        className="mt-0.5 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                        data-testid="launch-brandframe-toggle"
                      />
                      <div className="flex-1">
                        <span className="text-[11px] font-semibold text-indigo-900 flex items-center gap-1.5">
                          <Palette className="w-3 h-3 text-indigo-600" /> Brand-frame images
                        </span>
                        <span className="block text-[10.5px] text-indigo-700/70 mt-0.5">
                          {brandKits.length === 0
                            ? 'Create a Brand Kit to overlay your logo, colors & headline on each image.'
                            : 'Overlay your logo, brand colors & headline onto each AI photo (Spotlight · square).'}
                        </span>
                      </div>
                    </label>
                    {brandFrame && brandKits.length > 0 && (
                      <div className="mt-2 pl-6">
                        <Select value={brandKitId} onValueChange={setBrandKitId}>
                          <SelectTrigger className="text-xs h-8 bg-white" data-testid="launch-brandkit-select">
                            <SelectValue placeholder="Select a brand kit" />
                          </SelectTrigger>
                          <SelectContent>
                            {brandKits.map((k) => (
                              <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>

            <label className="flex items-start gap-2.5 rounded-md border border-pink-200 bg-pink-50/50 px-3 py-2.5 cursor-pointer">
              <Checkbox
                checked={draftEmails}
                onCheckedChange={(v) => setDraftEmails(!!v)}
                className="mt-0.5 data-[state=checked]:bg-pink-600 data-[state=checked]:border-pink-600"
                data-testid="launch-draftemails-toggle"
              />
              <div className="flex-1">
                <span className="text-xs font-semibold text-pink-900 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-pink-600" /> Draft matching emails
                </span>
                <span className="block text-[11px] text-pink-700/80 mt-0.5">
                  Newsletter + nurture sequence from the same strategy, saved into Email Marketing.
                </span>
                {draftEmails && (
                  <div className="mt-2">
                    <Select value={autoEnroll} onValueChange={setAutoEnroll}>
                      <SelectTrigger className="text-xs h-8 bg-white" data-testid="launch-autoenroll-select">
                        <SelectValue placeholder="Auto-enroll list (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No auto-enroll</SelectItem>
                        {emailLists.map((l) => (
                          <SelectItem key={l.id} value={l.id}>Auto-enroll new members of: {l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </label>

            {emailCtaUrl && (
              <p className="text-[11px] text-zinc-500 flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Lead-capture CTA will be attributed via tracked link.
              </p>
            )}

            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
              Will schedule <strong>{totalScheduled}</strong> post{totalScheduled === 1 ? '' : 's'}
              {genImages ? ' with AI images' : ''}{draftEmails ? ' and draft a matching email sequence' : ''}.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={handleLaunch}
            disabled={busy || loading || items.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="launch-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
            Launch campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LaunchCampaignDialog;
